import { Component } from 'react';
import type {
  ReactNode,
} from 'react';
import styled from 'styled-components';

import { Counter } from '../Counter';

import { LinkWrapper } from './LinkWrapper';

import {
  MENU_COLLAPSED,
  MENU_HOVER,
} from './constants';
import {
  ICON_WIDTH,
  ITEM_HEIGHT,
  StyledCounter,
  StyledTitleIcon,
  StyledTitleText,
  StyledCounterPoint,
} from './style';

import type {
  SidebarRenderType,
  SidebarLink,
  SidebarLinksGroup,
} from './types';

export const StyledLinksGroup = styled.div<{
  isFirstLevel: boolean;
  isGroupOpened?: boolean;
  isParentOfCurrent: boolean;
}>(({
  isFirstLevel,
  isParentOfCurrent,

  theme: {
    colors,
    sidebar,
  },
}) => ({
  position: 'relative',

  backgroundColor: isParentOfCurrent
    ? sidebar.ACTIVE_GROUP_BACKGROUND_COLOR
      ? colors[sidebar.ACTIVE_GROUP_BACKGROUND_COLOR]
      : 'transparent'
    : sidebar.INACTIVE_GROUP_BACKGROUND_COLOR
      ? colors[sidebar.INACTIVE_GROUP_BACKGROUND_COLOR]
      : 'transparent',

  marginBottom: isFirstLevel ? sidebar.FIRST_LEVEL_MARGIN_BOTTOM : 0,

  '&::before': {
    content: isParentOfCurrent ? '""' : undefined,
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: 2,
    backgroundColor: 'transparent',
  },

  ':hover': {
    color: colors.NEUTRAL_DEFAULT,
    backgroundColor: isFirstLevel ? colors.NEUTRAL_BACKGROUND : colors.NEUTRAL_INACTIVE,

    '&::before': isFirstLevel ? {
      content: '""',
    } : undefined,
  },

  ':active': {
    color: colors.NEUTRAL_DEFAULT,
    backgroundColor: isFirstLevel ? colors.NEUTRAL_BACKGROUND : colors.NEUTRAL_INACTIVE,

    '&::before': isFirstLevel ? {
      content: '""',
    } : undefined,
  },
}));

export const StyledToggleIcon = styled.div(({
  theme: {
    indents,
  },
}) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  paddingRight: indents.INDENT_1,
}));

export const StyledLinks = styled.div<{
  isFirstLevel: boolean;
  isHoverRender: boolean;
  isHidden: boolean;
}>(({
  isFirstLevel,
  isHoverRender,
  isHidden,
}) => ({
  paddingLeft: (isFirstLevel && isHoverRender) ? 8 : ICON_WIDTH,
  display: isHidden ? 'none' : undefined,
  paddingBottom: 12,
}));

export const StyledTitle = styled.div<{
  isHoverRender: boolean;
  isFirstLevel: boolean;
  isGroupOpened?: boolean;
  isParentOfCurrent: boolean;
}>(({
  isHoverRender,
  isFirstLevel,
  isParentOfCurrent,

  theme: {
    colors,
    fonts,
    sidebar,
  },
}) => ({
  ...fonts[isFirstLevel ? sidebar.FIRST_LEVEL_FONT : sidebar.DEFAULT_FONT],
  display: 'flex',
  position: 'relative',
  cursor: 'pointer',
  userSelect: 'none',
  color: (isFirstLevel && !isParentOfCurrent) ? colors.NEUTRAL_SECONDARY : colors.NEUTRAL_DEFAULT,
  paddingRight: isHoverRender ? 12 : 0,
  minHeight: isFirstLevel ? ITEM_HEIGHT : undefined,
  outline: 'none',
  marginLeft: isFirstLevel ? undefined : -8,
  paddingLeft: (isFirstLevel && !isHoverRender) ? undefined : 8,

  ':hover': {
    color: colors.NEUTRAL_DEFAULT,
  },

  ':active': {
    color: colors.NEUTRAL_DEFAULT,
  },

  '&::before': {
    content: (isParentOfCurrent && isFirstLevel) ? '""' : undefined,
    position: 'absolute',
    top: 0,
    left: 0,
    width: sidebar.BEFORE_TITLE_WIDTH,
    height: sidebar.BEFORE_TITLE_HEIGHT,
    backgroundColor: colors[sidebar.BEFORE_TITLE_BACKGROUND_COLOR],
  },
}));

export type LinksGroupProps = {
  linksGroup: SidebarLinksGroup;
  level: number;
  counters: {
    [key: string]: number | { count: number; isError?: boolean; };
  };
  countersMaxNumber?: number | null;
  showCountersInOpenedState: boolean;
  renderType: SidebarRenderType;
  isHover?: boolean;
  openedGroups: {
    [key: string]: boolean;
  };
  openedByDefault?: boolean;

  linksPath?: number[] | null;
  index: number;

  closeMenu: () => void;

  setHoverGroupIndex?: (hoveGroupIndex: number) => void;
  onToggleGroup?: (groupId: string, opened: boolean) => void;
};

const renderCounter = (counter: number, countersMaxNumber?: number | null, isError?: boolean) => (
  <StyledCounter>
    <Counter
      isError={isError}
      counter={counter}
      maxNumber={typeof countersMaxNumber === 'number' ? countersMaxNumber : undefined}
    />
  </StyledCounter>
);

export const renderGroupItem = (
  props: Omit<LinksGroupProps, 'linksGroup'>,
  link: SidebarLink | SidebarLinksGroup,
  linksPath: number[] | null | undefined,
): ReactNode => {
  const {
    level,
    index,
  } = props;

  const isActiveItem = linksPath
    ? linksPath[level] === index
    : false;

  const {
    type,
  } = link;

  switch (link.type) {
    case 'group':
    {
      return (
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        <LinksGroup
          {...props}
          linksGroup={link}
          linksPath={isActiveItem ? linksPath : undefined}
          key={link.id}
        />
      );
    }

    case 'link':
      return (
        <LinkWrapper
          {...props}
          isActiveItem={isActiveItem}
          link={link}
          key={link.id}
        />
      );

    default:
      throw new Error(`Unknown links group child type "${type}"`);
  }
};

export const isOpenedByPath = (props: LinksGroupProps): boolean => {
  if (props.linksPath) {
    return props.linksPath[props.level] === props.index;
  }

  return false;
};

const checkOpened = (props: LinksGroupProps): boolean => {
  if (isOpenedByPath(props)) {
    return true;
  }

  const isGroupOpened = props.openedGroups[props.linksGroup.id];

  if (typeof isGroupOpened === 'boolean') {
    return isGroupOpened;
  }

  return props.openedByDefault || false;
};

type LinksGroupState = {
  opened: boolean;
};

export class LinksGroup extends Component<LinksGroupProps, LinksGroupState> {
  nextLevel: number;

  constructor(props: LinksGroupProps) {
    super(props);

    this.state = {
      opened: checkOpened(props),
    };

    this.nextLevel = props.level + 1;
  }

  componentDidUpdate(prevProps: LinksGroupProps): void {
    const {
      linksPath,
    } = this.props;

    const {
      opened,
    } = this.state;

    if (
      linksPath !== prevProps.linksPath
      && !opened
    ) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({
        opened: isOpenedByPath(this.props),
      });
    }
  }

  toggleOpened = () => {
    const {
      opened: prevOpened,
    } = this.state;

    this.setState(({ opened }) => ({
      opened: !opened,
    }));

    const {
      linksGroup: {
        id,
      },

      onToggleGroup,
    } = this.props;

    if (onToggleGroup) {
      onToggleGroup(id, !prevOpened);
    }
  };

  renderGroupItem = (link: SidebarLink | SidebarLinksGroup, index: number): ReactNode => {
    const {
      counters,
      countersMaxNumber,
      linksPath,
      showCountersInOpenedState,
      renderType,
      openedGroups,
      openedByDefault,

      closeMenu,
      onToggleGroup,
    } = this.props;

    return renderGroupItem({
      level: this.nextLevel,
      counters,
      countersMaxNumber,
      index,
      showCountersInOpenedState,
      renderType,
      openedGroups,
      openedByDefault,

      closeMenu,
      onToggleGroup,
    }, link, linksPath);
  };

  setHoverGroupIndex = (): void => {
    const {
      index,
      setHoverGroupIndex,
    } = this.props;

    if (setHoverGroupIndex) {
      setHoverGroupIndex(index);
    }
  };

  render(): ReactNode {
    const {
      opened,
    } = this.state;

    const {
      level,
      linksGroup: {
        id,
        title,
        renderIcon,
        payload: {
          links,
        },
      },
      linksPath,
      index,

      counters,
      countersMaxNumber,
      showCountersInOpenedState,
      renderType,

      isHover,
    } = this.props;

    const isMenuVisible = renderType !== MENU_COLLAPSED;
    const isHoverRender = renderType === MENU_HOVER;
    const isNotHoverRender = !isHoverRender;

    const isGroupOpened = isHoverRender || (isMenuVisible ? opened : isHover);

    const isFirstLevel = level === 0;
    const counter = counters[id];
    const hasCounter = (typeof counter === 'number' && counter > 0) || (typeof counter === 'object' && counter.count > 0);
    const counterNum = typeof counter === 'number' ? counter : typeof counter === 'object' ? counter.count : 0;
    const counterError = typeof counter === 'object' && counter.isError;

    const isParentOfCurrent = (
      isFirstLevel
      && linksPath
      && linksPath[0] === index
    ) || false;

    return (
      <StyledLinksGroup
        isFirstLevel={isFirstLevel}
        isGroupOpened={isGroupOpened}
        isParentOfCurrent={isParentOfCurrent}
      >
        <StyledTitle
          isHoverRender={isHoverRender}
          isFirstLevel={isFirstLevel}
          isGroupOpened={isGroupOpened}
          isParentOfCurrent={isParentOfCurrent}
          onClick={this.toggleOpened}
          onKeyPress={this.toggleOpened}
          onMouseEnter={this.setHoverGroupIndex}
          role="button"
          tabIndex={0}
        >
          {
            isNotHoverRender && isFirstLevel && (
              <StyledTitleIcon
                $isMenuVisible={isMenuVisible}
                $isActive={isParentOfCurrent}
              >
                {renderIcon && renderIcon()}
              </StyledTitleIcon>
            )
          }

          {
            isMenuVisible && (
              <StyledTitleText>
                {title}
              </StyledTitleText>
            )
          }

          {
            !isMenuVisible && hasCounter && (
              <StyledCounterPoint $isError={counterError} />
            )
          }

          {
            isMenuVisible
            && hasCounter
            && (!opened || showCountersInOpenedState)
            && renderCounter(counterNum, countersMaxNumber, counterError)
          }

          {
            isNotHoverRender && isMenuVisible && (
              <StyledToggleIcon>
                {
                  opened ? (
                    <svg
                      width={16}
                      height={16}
                      viewBox="0 0 16 16"
                      fill="currentColor"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M8 5.53003L4 9.53003L4.94 10.47L8 7.4167L11.06 10.47L12 9.53003L8 5.53003Z"
                      />
                    </svg>
                  ) : (
                    <svg
                      width={16}
                      height={16}
                      viewBox="0 0 16 16"
                      fill="currentColor"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M11.06 5.53003L8 8.58336L4.94 5.53003L4 6.47003L8 10.47L12 6.47003L11.06 5.53003Z"
                      />
                    </svg>
                  )
                }
              </StyledToggleIcon>
            )
          }
        </StyledTitle>

        {
          isMenuVisible && (
            <StyledLinks
              isHoverRender={isHoverRender}
              isFirstLevel={isFirstLevel}
              isHidden={isNotHoverRender && !opened}
            >
              {links.map(this.renderGroupItem)}
            </StyledLinks>
          )
        }
      </StyledLinksGroup>
    );
  }
}

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
LinksGroup.defaultProps = {
  countersMaxNumber: null,

  linksPath: null,
  isHover: false,

  openedByDefault: false,

  setHoverGroupIndex: (): void => {},
  onToggleGroup: (): void => {},
};
